/**
 * Logger frontend centralizado.
 * Padroniza payload estruturado e envia erros ao Sentry quando disponível.
 *
 * Uso:
 *   logError("api_error", { endpoint, status, requestId });
 *   logWarn("silent_error_fallback", { error: err?.message });
 *   logInfo("event", { action: "click" });
 */

import * as Sentry from "@sentry/react";

function buildPayload(tag, payload = {}) {
  const base = {
    tag,
    timestamp: new Date().toISOString(),
    ...(payload && typeof payload === "object" ? payload : { message: payload }),
  };
  return base;
}

/**
 * Envia erro para Sentry. Resiliência: falha silenciosa se Sentry indisponível.
 * Adiciona contexto: service, página atual.
 *
 * @param {string} tag - Identificador do tipo de erro
 * @param {object} payload - Dados estruturados (error, url, status, requestId, etc.)
 */
function reportErrorToService(tag, payload) {
  try {
    Sentry.setTag("service", "snb-web");
    if (typeof window !== "undefined" && window.location?.pathname) {
      Sentry.setContext("ui", { page: window.location.pathname });
    }
    const err = payload?.error
      ? new Error(String(payload.error))
      : new Error(tag);
    Sentry.captureException(err, { extra: { tag, ...payload } });
  } catch (_) {
    // Sentry indisponível: não quebra o fluxo
  }
}

export function logInfo(tag, payload = {}) {
  const p = buildPayload(tag, payload);
  if (typeof console !== "undefined" && console.info) {
    console.info(`[SNB] ${tag}`, p);
  }
  return p;
}

export function logWarn(tag, payload = {}) {
  const p = buildPayload(tag, payload);
  if (typeof console !== "undefined" && console.warn) {
    console.warn(`[SNB] ${tag}`, p);
  }
  reportErrorToService(tag, p);
  return p;
}

export function logError(tag, payload = {}) {
  const p = buildPayload(tag, payload);
  if (typeof console !== "undefined" && console.error) {
    console.error(`[SNB] ${tag}`, p);
  }
  reportErrorToService(tag, p);
  return p;
}
